'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import DesktopNav from '@/components/layout/DesktopNav';
import MobileNavDrawer from '@/components/layout/MobileNavDrawer';
import { SIGNIN_URL } from '@/lib/urls';
import { useSignupCtas, useSignupModeState } from '@/components/utils/SignupModeProvider';
import { useCtaLinkProps } from '@/components/utils/useCtaLinkProps';
import { cn } from '@/lib/twUtils';

/**
 * Quanto è largo il logo esteso in barra stretta.
 *
 * Il disegno è largo 7193×903, quasi otto volte la sua altezza: alto 20px
 * (`h-5`) ne occupa 160. Non lo misuriamo dal DOM perché finché non decidiamo
 * di mostrarlo è `display:none`, e da lì larghezza non se ne legge.
 */
const WORDMARK_WIDTH = 160;

/** Aria fra il logo e la CTA, oltre allo stacco che già c'è fra le due colonne. */
const WORDMARK_AIR = 6;

/**
 * Barra di navigazione del sito: fissa, vetro smerigliato, alta 68px.
 * L'altezza è replicata da <NavigationBarSpacing /> per le pagine il cui hero
 * non ha già un padding superiore dedicato.
 */
const NavigationBar = () => {
  const pathname = usePathname();
  const { primary } = useSignupCtas();
  const { isSettled } = useSignupModeState();
  const ctaProps = useCtaLinkProps('navbar');
  // Memorizziamo la rotta su cui il drawer è stato aperto invece di un
  // booleano: al cambio pagina si chiude da solo, senza effetti collaterali.
  const [openedOnPath, setOpenedOnPath] = useState<string | null>(null);
  const isMobileMenuOpen = openedOnPath !== null && openedOnPath === pathname;

  const closeMobileMenu = () => setOpenedOnPath(null);
  const toggleMobileMenu = () => setOpenedOnPath(isMobileMenuOpen ? null : pathname);

  /* Il logo per esteso anche su schermo stretto, ma solo dove ci sta ----------
     Quanto spazio resti non lo sa il CSS: dipende dall'etichetta della CTA,
     che la decide il backend. «Prova gratis» lascia 168px su un telefono da
     390, «Prenota una demo» ne lascia 125 — nel primo caso il logo esteso ci
     entra, nel secondo no. Quindi lo misuriamo: larghezza della barra, meno
     i suoi margini, meno la colonna dei bottoni. L'osservatore guarda anche
     quella colonna, così quando l'etichetta cambia il conto si rifà da sé.

     Si aspetta che la modalità sia decisa (`isSettled`) per non far ballare
     il logo due volte: prima con l'etichetta di default, poi con quella
     vera. Così il logo compare quando compaiono i bottoni, in un colpo solo. */
  const bar = useRef<HTMLDivElement>(null);
  const actions = useRef<HTMLDivElement>(null);
  const [wordmarkFits, setWordmarkFits] = useState(false);

  useEffect(() => {
    const row = bar.current;
    const right = actions.current;
    if (!row || !right) return;
    const measure = () => {
      const style = getComputedStyle(row);
      const free =
        row.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight) -
        right.getBoundingClientRect().width -
        (parseFloat(style.columnGap) || 0);
      setWordmarkFits(isSettled && free >= WORDMARK_WIDTH + WORDMARK_AIR);
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(row);
    watch.observe(right);
    return () => watch.disconnect();
  }, [isSettled]);

  // Con il drawer aperto la pagina sotto non deve scorrere
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 border-b border-edge-soft',
        isMobileMenuOpen ? 'bg-white' : 'bg-white/95 backdrop-blur-[14px]'
      )}
    >
      {/* `relative` sul contenuto: su iOS il backdrop-filter della barra fissa
          può mangiarsi i tap dei figli se restano nello stesso piano. */}
      <div
        ref={bar}
        className="relative mx-auto flex h-[4.25rem] max-w-[77.5rem] items-center gap-3 px-6 lg:gap-8"
      >
        <Link
          href="/"
          data-wordmark={wordmarkFits || undefined}
          className="group shrink-0 rounded-md ds-focus"
          aria-label="Trovanorma — home"
        >
          {/* Il logo esteso è largo quasi otto volte la sua altezza: dai 640px
              in su ci sta sempre, sotto dipende da quanto occupa la CTA e lo
              decide la misura qui sopra. Dove non ci sta resta il simbolo. */}
          <Image
            src="/images/logo-icon-orange.svg"
            width={32}
            height={32}
            priority
            alt=""
            className="h-7 w-auto group-data-[wordmark]:hidden sm:hidden"
          />
          {/* In barra stretta è più basso: a 26px non entrerebbe su nessun
              telefono, a 20 sì da 390px in su. */}
          <Image
            src="/images/logo-horizontal-orange-blue.svg"
            width={176}
            height={27}
            priority
            alt=""
            className="hidden h-5 w-auto group-data-[wordmark]:block sm:block sm:h-[1.625rem]"
          />
        </Link>

        <div className="hidden flex-1 lg:block">
          <DesktopNav />
        </div>

        <div ref={actions} className="ml-auto flex shrink-0 items-center gap-3 lg:gap-4">
          <a
            href={SIGNIN_URL}
            className="hidden rounded-sm text-[0.9375rem] font-medium text-ink transition-colors hover:text-accent ds-focus md:inline"
          >
            Accedi
          </a>
          {/* Come le altre CTA: tiene il posto ma resta invisibile finché la
              modalità non è certa, così l'etichetta non cambia sotto gli occhi. */}
          <a
            {...ctaProps(primary)}
            data-cta-pending={!isSettled || undefined}
            className={cn(
              'rounded-ds bg-primary px-4 py-2.5 text-[0.875rem] font-semibold whitespace-nowrap text-white',
              'transition-colors hover:bg-accent ds-focus sm:px-5 sm:text-[0.90625rem]',
              !isSettled && 'invisible opacity-0'
            )}
          >
            {primary.label}
          </a>
          <button
            type="button"
            onClick={toggleMobileMenu}
            aria-controls="mobile-menu"
            aria-expanded={isMobileMenuOpen}
            className="grid h-10 w-10 place-items-center rounded-ds-sm border border-edge text-primary ds-focus lg:hidden"
          >
            <span className="sr-only">{isMobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}</span>
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && <MobileNavDrawer onLinkClick={closeMobileMenu} />}
    </header>
  );
};

export default NavigationBar;
